import {
  Box,
  Flex,
  IconButton,
  Text,
  Button,
  Input,
  Heading,
  DownloadTrigger,
  Textarea,
  Spinner, 
  Center
} from "@chakra-ui/react";

import { Document, Page, pdfjs } from "react-pdf";
import { useEffect, useState } from "react";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { AiOutlineFullscreen } from "react-icons/ai";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { FixedSizeList as List } from "react-window";

// Use local worker file that we copied to public directory
pdfjs.GlobalWorkerOptions.workerSrc = '/static/js/pdf.worker.min.js';

console.log('PDF.js worker src:', pdfjs.GlobalWorkerOptions.workerSrc);

export default function PDFViewer({
  pdfFile,
  mode,
  search,
  setSearch,
  setIsUploadOpen,
  editedPdfFile,
  handleEdit,
  editedPdfName,
  error,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [visiblePage, setVisiblePage] = useState(1);
  const [numberOfInstances, setNumberOfInstances] = useState(0);
  const [renderedPages, setRenderedPages] = useState(new Set());
  const [pdfError, setPdfError] = useState(null);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPdfError(null); // Clear any previous errors
  }

  function onDocumentLoadError(error) {
    console.error("PDF loading error:", error);
    console.error("Error details:", {
      message: error.message,
      name: error.name,
      stack: error.stack
    });
    
    // If this is a worker-related error, try setting CDN worker
    if (error.message && error.message.includes('worker')) {
      console.log('Worker error detected, setting CDN worker...');
      pdfjs.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.2.67/pdf.worker.min.js';
    }
    
    setPdfError(error.message || "Failed to load PDF file");
  }

  const pageHeight = 1000;

  // Reset error when PDF file changes
  useEffect(() => {
    console.log("PDF file changed:", pdfFile ? {
      type: typeof pdfFile,
      isFile: pdfFile instanceof File,
      isBlob: pdfFile instanceof Blob,
      name: pdfFile.name || 'unknown',
      size: pdfFile.size || 'unknown'
    } : 'null');
    setPdfError(null);
    setNumPages(null);
    
    // Test if file can be read
    if (pdfFile instanceof File) {
      console.log("Testing file readability...");
      const reader = new FileReader();
      reader.onload = () => {
        console.log("File is readable, size:", reader.result.byteLength);
      };
      reader.onerror = (error) => {
        console.error("File read error:", error);
        setPdfError("Failed to read the selected file");
      };
      reader.readAsArrayBuffer(pdfFile);
    }
  }, [pdfFile]);

  useEffect(() => {
    if (!search) return;

    const pages = document.querySelectorAll(".react-pdf__Page");

    const normalizedSearch = search
      .replace(/\r\n/g, "")
      .replace(/\n+/g, "")
      .trim();
    if (normalizedSearch.length === 0) {
      return;
    }
    pages.forEach((page) => {
      const spans = Array.from(
        page.querySelectorAll(".react-pdf__Page__textContent span")
      );

      let fullText = "";
      const spanRanges = spans.map((span) => {
        const start = fullText.length;
        const text = span.textContent || "";
        fullText += text;
        return { span, start, end: start + text.length, text };
      });

      const normalizedFullText = fullText.replace(/\r?\n/g, "\n");

      // Find all match ranges in the full text
      const matchRanges = [];
      let matchIndex = 0;
      while (
        (matchIndex = normalizedFullText.indexOf(
          normalizedSearch,
          matchIndex
        )) !== -1
      ) {
        matchRanges.push({
          start: matchIndex,
          end: matchIndex + normalizedSearch.length,
        });
        matchIndex += normalizedSearch.length;
      }

      // Clear old highlights
      spanRanges.forEach(({ span, text }) => {
        span.innerHTML = text;
      });

      // Apply highlights
      spanRanges.forEach(({ span, start, end, text }) => {
        const highlights = [];

        matchRanges.forEach(({ start: mStart, end: mEnd }) => {
          if (end <= mStart || start >= mEnd) return;

          const localStart = Math.max(mStart - start, 0);
          const localEnd = Math.min(mEnd - start, text.length);
          highlights.push({ start: localStart, end: localEnd });
        });

        if (highlights.length === 0) return;

        // Merge and apply highlights in order
        highlights.sort((a, b) => a.start - b.start);

        let result = "";
        let currentIndex = 0;

        highlights.forEach(({ start: hStart, end: hEnd }) => {
          result += text.slice(currentIndex, hStart);
          result += `<mark style="background: yellow">${text.slice(
            hStart,
            hEnd
          )}</mark>`;
          currentIndex = hEnd;
        });

        result += text.slice(currentIndex);
        span.innerHTML = result;
      });
    });
  }, [search, visiblePage]);

  useEffect(() => {
    if (!pdfFile || !search) return;

    const countSearchMatches = async () => {
      try {
        let loadingTask;

        if (typeof pdfFile === "string") {
          loadingTask = pdfjs.getDocument(pdfFile);
        } else if (pdfFile instanceof File || pdfFile instanceof Blob) {
          const arrayBuffer = await pdfFile.arrayBuffer();
          loadingTask = pdfjs.getDocument({ data: arrayBuffer });
        } else {
          console.error("Unsupported pdfFile format");
          return;
        }

        const pdf = await loadingTask.promise;
        let totalMatches = 0;

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();

          const pageText = textContent.items.map((item) => item.str).join("");
          const normalizedPageText = pageText.replace(/\s+/g, "");
          const normalizedSearch = search.replace(/\s+/g, "");

          // Strict letter-for-letter matching (case-sensitive)
          let index = 0;
          while (
            (index = normalizedPageText.indexOf(normalizedSearch, index)) !== -1
          ) {
            totalMatches++;
            index += normalizedSearch.length;
          }
        }
        setNumberOfInstances(totalMatches);
      } catch (error) {
        console.error("Error counting matches:", error);
        setNumberOfInstances(0);
      }
    };

    countSearchMatches();
  }, [pdfFile, search]);

  return (
    <Box
      height="100%"
      border="1px solid lightgray"
      borderRadius="md"
      boxShadow={"md"}
      w="3xl"
      p={2}
      bgColor="gray.100"
    >
      <Heading size="xl" fontWeight="bold" mb={2}>
        {mode === "original" ? "ORIGINAL PDF" : "FINAL PDF"}
      </Heading>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        height="95%"
      >
        <Flex gap={3} align={"center"} justify={"center"} mb={2}>
          {mode === "original" ? (
            <>
              <Button
                size="sm"
                variant="solid"
                onClick={() => {
                  setIsUploadOpen(true);
                }}
                _hover={{
                  transform: "scale(1.02)",
                  boxShadow: "md",
                }}
                transition="all 0.2s ease-in-out"
              >
                Upload PDF
              </Button>
              <Textarea
                w="sm"
                bg="white"
                // borderColor={
                //   errors.email || errors.login ? "red.300" : "gray.300"
                // }
                value={search}
                placeholder="Some text..."
                onChange={(e) => setSearch(e.target.value)}
              />
              <Text>
                Total matches:{" "}
                {numberOfInstances && search.length ? numberOfInstances : 0}
              </Text>
              <Button
                size="sm"
                variant="solid"
                onClick={handleEdit}
                disabled={!pdfFile || !search.trim()}
                _hover={{
                  transform: "scale(1.02)",
                  boxShadow: "md",
                }}
                transition="all 0.2s ease-in-out"
              >
                Edit PDF
              </Button>
            </>
          ) : (
            <>
              <a href={editedPdfFile} download={editedPdfName}>
                <Button
                  w="120px"
                  size="sm"
                  variant="solid"
                  disabled={!editedPdfFile}
                  //onClick={handleLogin}
                  _hover={{
                    transform: "scale(1.02)",
                    boxShadow: "md",
                  }}
                  transition="all 0.2s ease-in-out"
                >
                  Download PDF
                </Button>
              </a>
              {error && (
                <Text color="red.500" fontSize="md" fontWeight={"bold"}>
                  Error: {error}
                </Text>
              )}
            </>
          )}
        </Flex>

        <Box w="100%" mb="2" >
          {pdfError ? (
            <Center h="400px" flexDirection="column">
              <Text color="red.500" fontSize="lg" fontWeight="bold" mb={2}>
                PDF Loading Error
              </Text>
              <Text color="red.400" fontSize="sm" textAlign="center" maxW="md">
                {pdfError}
              </Text>
              <Button 
                mt={4} 
                size="sm" 
                variant="outline" 
                onClick={() => {
                  setPdfError(null);
                  setIsUploadOpen(true);
                }}
              >
                Try Another File
              </Button>
            </Center>
          ) : (
            <Document 
              file={pdfFile} 
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={<Center h="200px"><Spinner size="lg" /></Center>}
            >
              <Box overflow="auto" height="850px" >
                {numPages && (
                  <List
                    height={850}
                    itemCount={numPages}
                    itemSize={pageHeight}
                    width="100%"
                    onItemsRendered={({ visibleStartIndex }) => {
                      setVisiblePage(visibleStartIndex + 1);
                    }}
                    children={createRowRenderer}
                  />
                )}
              </Box>
            </Document>
          )}
        </Box>

        {/* Arrows on the bottom inside the same container */}
        <Flex justify="center" align="center" gap={4}>
          {/* <IconButton
          onClick={() => changePage(-1)}
          disabled={pageNumber <= 1}
          variant="outline"
          size={"md"}
        >
          <FaArrowLeft />
        </IconButton>
      

        <IconButton
          onClick={() => changePage(1)}
          disabled={pageNumber >= numPages}
          aria-label="Search database"
          variant="outline"
          size={"md"}
        >
          <FaArrowRight />
        </IconButton> */}
          <Text fontSize="sm">
            Page {visiblePage} of {numPages || "--"}
          </Text>
        </Flex>
      </Box>
     
    </Box>
  );
}

const createRowRenderer = ({ index, style }) => {
  const pageNumber = index + 1;

  return (
    <div style={style}>
      <Page
        pageNumber={pageNumber}
        width={700}
        loading={<Text textAlign="center">Loading page {pageNumber}...</Text>}
        error={
          <Center h="200px" flexDirection="column">
            <Text color="red.500" fontSize="sm">
              Failed to load page {pageNumber}
            </Text>
          </Center>
        }
      />
    </div>
  );
};

